import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { MessageSquare, Send, Loader2, Clock, X } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";

function fmtSeconds(s) {
    if (s == null || isNaN(s)) return "0:00";
    const total = Math.floor(s);
    const m = Math.floor(total / 60);
    const sec = total % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
}

/**
 * Comments thread for a file.
 * Variants:
 *  - mode="portal": authenticated user, no need for name/email
 *  - mode="share":  public visitor; requires name (+ optional email)
 *
 * If `videoRef` is provided AND the file is a video, comments can be tied to a
 * specific second. The current playback time is auto-captured on submit; existing
 * comments show a clickable timestamp that seeks the video.
 */
export default function FileComments({ fileId, mode = "portal", shareToken, videoRef }) {
    const [comments, setComments] = useState(null);
    const [body, setBody] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [pinnedTime, setPinnedTime] = useState(null); // seconds, or null = no pin

    const baseUrl =
        mode === "share"
            ? `/share/${shareToken}/files/${fileId}/comments`
            : mode === "fshare"
            ? `/fshare/${shareToken}/comments`
            : `/files/${fileId}/comments`;
    const publicClient = mode === "share" || mode === "fshare";

    const load = async () => {
        try {
            const { data } = publicClient
                ? await api.get(baseUrl, { headers: { Authorization: "" } })
                : await api.get(baseUrl);
            setComments(data);
        } catch (e) {
            setComments([]);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId, mode, shareToken]);

    // Auto-capture currentTime when user starts typing (only for video files)
    const captureCurrentTime = () => {
        const v = videoRef?.current;
        if (!v || isNaN(v.currentTime)) return;
        setPinnedTime(v.currentTime);
    };

    const seekTo = (t) => {
        const v = videoRef?.current;
        if (!v) return;
        try {
            v.currentTime = t;
            v.play().catch(() => {});
        } catch {
            // ignore
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!body.trim()) return;
        if (publicClient && !name.trim()) {
            toast.error("Vul je naam in om een vraag of opmerking te plaatsen.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { body: body.trim() };
            if (publicClient) {
                payload.author_name = name.trim();
                if (email.trim()) payload.author_email = email.trim();
            }
            if (pinnedTime != null && !isNaN(pinnedTime)) {
                payload.timestamp_seconds = Number(pinnedTime.toFixed(2));
            }
            const config = publicClient
                ? { headers: { Authorization: "" } }
                : {};
            await api.post(baseUrl, payload, config);
            toast.success("Opmerking geplaatst");
            setBody("");
            setPinnedTime(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const hasVideo = !!videoRef?.current;

    return (
        <div className="border-t border-zinc-200 pt-4" data-testid="file-comments">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">
                <MessageSquare className="w-3 h-3" />
                Opmerkingen & vragen{comments ? ` (${comments.length})` : ""}
            </div>

            {comments === null ? (
                <div className="text-zinc-400 text-sm flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Laden…
                </div>
            ) : comments.length === 0 ? (
                <div className="text-sm text-zinc-500 mb-3">
                    Nog geen opmerkingen.
                </div>
            ) : (
                <ul className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                    {comments.map((c) => (
                        <li
                            key={c.id}
                            className="border border-zinc-200 rounded-md p-3 bg-zinc-50"
                            data-testid={`comment-${c.id}`}
                        >
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                                <div className="text-sm font-medium truncate flex items-center gap-2">
                                    {c.author_name}
                                    {c.source === "share" && (
                                        <span className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm">
                                            Bezoeker
                                        </span>
                                    )}
                                </div>
                                <div className="text-[10px] font-mono uppercase text-zinc-500">
                                    {new Date(c.created_at).toLocaleString("nl-BE")}
                                </div>
                            </div>
                            {c.timestamp_seconds != null && (
                                <button
                                    type="button"
                                    onClick={() => seekTo(c.timestamp_seconds)}
                                    disabled={!hasVideo}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 mb-1.5 rounded-sm bg-[#0044FF]/10 text-[#0044FF] text-xs font-mono hover:bg-[#0044FF]/20 disabled:opacity-50 disabled:cursor-default"
                                    data-testid={`comment-timestamp-${c.id}`}
                                    title={hasVideo ? "Spring naar dit moment in de video" : ""}
                                >
                                    <Clock className="w-3 h-3" />
                                    {fmtSeconds(c.timestamp_seconds)}
                                </button>
                            )}
                            <p className="text-sm text-zinc-800 whitespace-pre-wrap">{c.body}</p>
                        </li>
                    ))}
                </ul>
            )}

            <form onSubmit={submit} className="space-y-2" data-testid="comment-form">
                {publicClient && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input
                            placeholder="Je naam *"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            data-testid="comment-name"
                        />
                        <Input
                            placeholder="E-mail (optioneel)"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="comment-email"
                        />
                    </div>
                )}

                {/* Timestamp pin (only for videos) */}
                {hasVideo && (
                    <div className="flex items-center gap-2">
                        {pinnedTime != null ? (
                            <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#0044FF]/10 border border-[#0044FF]/30 text-[#0044FF] text-xs font-mono"
                                data-testid="comment-pinned-time"
                            >
                                <Clock className="w-3 h-3" />
                                Op {fmtSeconds(pinnedTime)}
                                <button
                                    type="button"
                                    onClick={() => setPinnedTime(null)}
                                    className="ml-1 text-[#0044FF] hover:text-red-600"
                                    aria-label="Tijd loskoppelen"
                                    data-testid="comment-unpin-time"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ) : (
                            <button
                                type="button"
                                onClick={captureCurrentTime}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-zinc-200 hover:border-[#0044FF] hover:text-[#0044FF] text-zinc-600 text-xs font-mono"
                                data-testid="comment-pin-time"
                                title="Koppel deze opmerking aan het huidige moment in de video"
                            >
                                <Clock className="w-3 h-3" />
                                Koppel aan huidig moment
                            </button>
                        )}
                    </div>
                )}

                <div className="flex gap-2 items-end">
                    <Textarea
                        rows={2}
                        placeholder={
                            hasVideo
                                ? "Stel een vraag of plaats een opmerking… (pauzeer de video en klik op de klok om een moment te koppelen)"
                                : "Stel een vraag of plaats een opmerking…"
                        }
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        onFocus={() => {
                            // auto-pin when video is paused and no time is set yet
                            const v = videoRef?.current;
                            if (v && v.paused && !v.ended && pinnedTime == null && v.currentTime > 0) {
                                setPinnedTime(v.currentTime);
                            }
                        }}
                        className="flex-1 text-sm"
                        data-testid="comment-body"
                    />
                    <Button
                        type="submit"
                        disabled={submitting || !body.trim()}
                        className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                        data-testid="comment-submit"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                </div>
            </form>
        </div>
    );
}
